import React, { useEffect } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert, { AlertProps } from "@src/common/alert";
import { GenerativeModel, useGenerativeModelAPI } from "../../api/generative_model_api";
import { useGenerativeModelTestScenarioAPI } from "../../api/vault_test_scenario_api";

interface VaultIndexProps {
}

const VaultIndex: React.FC<VaultIndexProps> = (props) => {
    const [queryParams] = useSearchParams();
    const [data, setData] = React.useState<GenerativeModel[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const genModelAPI = useGenerativeModelAPI();
    const vaultAPI = useGenerativeModelTestScenarioAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Name",
            selector: (row: GenerativeModel) => row.name || "",
            sortable: true,
            width: "250px",
        },
        {
            name: "Status",
            selector: (row: GenerativeModel) => row.status || "",
            sortable: true,
            width: "90px",
        },
        {
            name: "Public",
            selector: (row: GenerativeModel) => row.is_public === true ? "Yes" : "No",
            sortable: true,
            width: "90px",
        },
        // {
        //     name: "Access Count",
        //     selector: (row: GenerativeModel) => row.stat_access_count || "",
        //     sortable: true,
        //     width: "150px"
        // },
        {
            name: "Usage Count",
            selector: (row: GenerativeModel) => row.stat_usage_count || "",
            sortable: true,
            width: "90px",
        },
        {
            name: "Vault Actions",
            cell: (row: GenerativeModel) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => navigate(`/vault/variations?generative_model_id=${row.id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        View Variations
                    </button>
                    <button
                        onClick={() => handleStatusToggle(row)}
                        className={`${row.status ? "bg-yellow-500 hover:bg-yellow-700" : "bg-green-500 hover:bg-green-700"} text-white font-bold py-1 px-3 rounded text-sm w-[6.5rem]`}
                    >
                        {row.status === "ACTIVE" ? "Deactivate" : "Activate"}
                    </button>
                    <button
                        onClick={() => handleDelete(row.id)}
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        Delete Vault
                    </button>
                </div>
            ),
            width: "420px",
        },
        {
            name: "Test Scenario Actions",
            cell: (row: GenerativeModel) => (
                <div className="flex space-x-2">
                    {/* <button
                        onClick={() => handleCreateTestScenario(row)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm">
                        Create New
                    </button> */}
                    <button
                        onClick={() => navigate(`/vault/test-scenarios?generative_model_id=${row.id}`)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm">
                        View Test Scenarios
                    </button>
                </div>
            ),
            width: "250px",
        }
    ];

    const handleCreateTestScenario = (vault: GenerativeModel) => {
        if (!vault.id) return;

        vaultAPI.createScenario(vault.id)
            .then(() => {
                setStatus({
                    type: "success",
                    message: "Test scenario created successfully"
                });
                fetchVaults();
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: "Failed to create test scenario: " + error.message
                });
            });
    }

    const fetchVaults = () => {
        if (!queryParams.get("user_id")) {
            return;
        }
        setLoading(true);

        // @ts-ignore
        genModelAPI.getModelsForUser(queryParams.get("user_id"))
            .then((response) => {
                setData(response);
                setLoading(false);
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: "Failed to load vaults: " + error.message
                });
                setLoading(false);
            });
    };

    const handleStatusToggle = (vault: GenerativeModel) => {
        if (!vault.id) return;

        const newStatus = vault.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        genModelAPI.updateModel(vault.id, { ...vault, status: newStatus })
            .then(() => {
                setStatus({
                    type: "success",
                    message: `Vault ${newStatus === "ACTIVE" ? "activated" : "deactivated"} successfully`
                });
                fetchVaults();
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: `Failed to ${newStatus === "ACTIVE" ? "activate" : "deactivate"} vault: ${error.message}`
                });
            });
    };

    const handleDelete = (id?: string) => {
        if (!id) return;

        if (window.confirm("Are you sure you want to delete this vault? This action cannot be undone.")) {
            genModelAPI.deleteModel(id)
                .then(() => {
                    setStatus({
                        type: "success",
                        message: "Vault deleted successfully"
                    });
                    fetchVaults();
                })
                .catch((error) => {
                    setStatus({
                        type: "error",
                        message: "Failed to delete vault: " + error.message
                    });
                });
        }
    };

    useEffect(() => {
        fetchVaults();
    }, [queryParams]);

    return (
        <AppContainer>
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">Vaults</h1>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    progressPending={loading}
                    progressComponent={<div className="py-6">Loading vaults...</div>}
                    noDataComponent={<div className="py-6">No vaults found</div>}
                />

            </div>
        </AppContainer>
    );
};

export default VaultIndex;